(function () {
  'use strict';

  /* Singleton guard — this module registers document-level capture
     listeners; re-evaluation (theme reinjection, app interference) would
     duplicate them and fire applyCode/removeCode twice per click. */
  if (window._ddDiscountCodeInit) return;
  window._ddDiscountCodeInit = true;

  var INPUT_SEL = '[data-dd-discount-input]';
  var APPLY_SEL = '[data-dd-discount-apply]';
  var CONTAINER_SEL = '[data-dd-discount]';
  var FEEDBACK_SEL = '[data-dd-discount-feedback]';
  var CHIP_HOST_SEL = '[data-dd-discount-chips]';
  var CHIP_DISMISS_MS = 5000;
  var _isApplying = false;
  var _applyingTimeout = null;
  var APPLY_SAFETY_TIMEOUT_MS = 15000;
  var _discountOpToken = 0;
  var _failedCodes = [];

  /* "Pending" codes — entered by the shopper, recognized by Shopify,
     and stored on cart.discount_codes with `applicable: false`. The
     code IS attached to the cart server-side and will activate
     automatically once the unmet condition (qualifying product,
     minimum order, customer eligibility, etc.) is satisfied. We track
     normalized→raw here so renderChips() can detect activation when a
     real applied badge materializes (e.g., after the shopper adds the
     qualifying item and /cart/add.js leaves discount_codes intact)
     and emit a positive "Discount activated!" toast — closing the
     loop on the apply-time "not active yet" message instead of
     leaving the shopper to wonder whether the original toast was
     correct.

     Distinct from `_failedCodes` (which models "we tried this and it
     didn't fit — hide in 5s") because pending codes have an
     indefinite lifetime: they may activate within seconds, minutes,
     or never (if the shopper never satisfies the condition).

     Persisted to sessionStorage so same-tab navigation (cart drawer
     → PDP → cart drawer) keeps the activation-detection capability
     alive: shopper enters pending code in drawer, navigates to a
     product page, adds the qualifying item, drawer reopens — the
     newly-applied chip materializes AND we emit "Discount
     activated!" because the pending state survived the navigation.
     Without persistence, page nav would silently forget which codes
     to celebrate when they activate.

     Reconciled with server state on init via reconcilePendingFromCart
     so cross-session pending codes (shopper applied yesterday, never
     met conditions, came back today) become visible as pending
     chips immediately rather than as invisible-until-they-activate
     server state. */
  var _pendingCodes = {};
  var PENDING_STORAGE_KEY = 'DDPendingCodes';

  function loadPendingCodesFromSession() {
    try {
      var raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach(function (k) {
          if (typeof parsed[k] === 'string' && parsed[k]) _pendingCodes[k] = parsed[k];
        });
      }
    } catch (_e) { /* sessionStorage disabled or corrupt — fall back to in-memory only */ }
  }

  function persistPendingCodes() {
    try { sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(_pendingCodes)); } catch (_e) {}
  }

  function setPendingCode(normalizedCode, rawCode) {
    if (!normalizedCode) return;
    _pendingCodes[normalizedCode] = rawCode || normalizedCode;
    persistPendingCodes();
  }

  function deletePendingCode(normalizedCode) {
    if (!normalizedCode) return;
    if (_pendingCodes[normalizedCode]) {
      delete _pendingCodes[normalizedCode];
      persistPendingCodes();
    }
  }

  /* Gift-card removal in-flight set — keyed by Storefront API
     `applicationId` (the GID returned in `appliedGiftCard.id`). The X
     button on a gift-card badge is also disabled in the DOM while the
     mutation is in flight, so this set is a defense-in-depth guard
     against the badge being clicked twice through accelerated event
     delivery (e.g. an iOS double-tap synthesizing two clicks before
     `disabled = true` is observed). The ID is removed in the final
     `.then()` of the chain so success and failure both clear it. */
  var _giftCardOpsInFlight = (typeof Set === 'function') ? new Set() : null;

  function _giftCardOpsInFlightHas(id) {
    if (_giftCardOpsInFlight) return _giftCardOpsInFlight.has(id);
    return false;
  }
  function _giftCardOpsInFlightAdd(id) {
    if (_giftCardOpsInFlight) _giftCardOpsInFlight.add(id);
  }
  function _giftCardOpsInFlightDelete(id) {
    if (_giftCardOpsInFlight) _giftCardOpsInFlight['delete'](id);
  }

  function startApplyingTimeout() {
    clearTimeout(_applyingTimeout);
    _applyingTimeout = setTimeout(function () {
      if (_isApplying) {
        console.warn('[dd-discount-code] _isApplying stuck for 15s — force-resetting');
        _isApplying = false;
        var containers = document.querySelectorAll(CONTAINER_SEL);
        containers.forEach(function (c) {
          c.classList.remove('is--applying');
          var btn = c.querySelector(APPLY_SEL);
          if (btn) {
            btn.disabled = false;
            btn.textContent = btn.dataset.originalText || 'Apply';
          }
        });
      }
      _applyingTimeout = null;
    }, APPLY_SAFETY_TIMEOUT_MS);
  }

  function clearApplyingTimeout() {
    clearTimeout(_applyingTimeout);
    _applyingTimeout = null;
  }
  function isEnterKey(e) {
    return !!e && (e.key === 'Enter' || e.code === 'NumpadEnter');
  }
  var _isCartPage = /\/cart\b/.test(window.location.pathname);

  /* ── Dawn section rendering helpers ────────────── */

  function getRequestSections() {
    if (_isCartPage) {
      /* Include 'cart-drawer' on /cart: both surfaces coexist in the DOM and
         the drawer's badges/stamps go stale if only page sections refresh
         (stale badges fed an incomplete kept-codes list and wiped codes). */
      var secs = ['cart-drawer', 'cart-icon-bubble'];
      var el1 = document.getElementById('main-cart-items');
      var el2 = document.getElementById('main-cart-footer');
      if (el1 && el1.dataset.id) secs.push(el1.dataset.id);
      if (el2 && el2.dataset.id) secs.push(el2.dataset.id);
      return secs;
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  /*
   * Refresh cart sections after a Storefront-API gift card mutation.
   * The Storefront GraphQL response doesn't carry section HTML, so we
   * follow up with `/cart?sections=...` to pull the same section bundle
   * that `/cart/update.js?sections=...` would have returned. Result is
   * forwarded to `ddCartHelpers.replaceCartSections` (the same painter
   * the existing AJAX-cart flow uses) so badge + total updates appear
   * identically across drawer and cart-page surfaces.
   *
   * Requests added per gift card apply: 1 (a single same-origin GET).
   * Total apply budget stays within rate-limit-awareness.mdc's <=3
   * Shopify requests per action: /cart/update.js (1, no-op for gift
   * cards) + Storefront cartGiftCardCodesAdd (1) + this section
   * refresh (1).
   */
  function refreshCartSectionsAfterGiftCard() {
    var sections = getRequestSections();
    if (!sections || !sections.length) return Promise.resolve();
    var url = '/cart?sections=' + encodeURIComponent(sections.join(','));
    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        if (window.ddCartHelpers && typeof window.ddCartHelpers.replaceCartSections === 'function') {
          window.ddCartHelpers.replaceCartSections(data);
        }
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { reason: 'giftcard-apply', source: 'dd-discount-code' },
          }));
        } catch (e) { /* IE/legacy CustomEvent — silent */ }
      });
  }

  function mutateDiscount(body, actionName) {
    var payload = Object.assign({}, body, {
      sections: getRequestSections(),
      sections_url: '/cart'
    });

    if (window.ddCartHelpers && typeof window.ddCartHelpers.mutateCartUpdate === 'function') {
      return window.ddCartHelpers.mutateCartUpdate(payload, {
        sections: getRequestSections(),
        action: actionName || 'discount-update',
        coalesceKey: 'discount-op'
      });
    }

    if (window.ddCartHelpers && typeof window.ddCartHelpers.recordRequest === 'function') {
      window.ddCartHelpers.recordRequest(cartUpdateUrl(), actionName || 'discount-update', 'discount-fallback');
    }
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart update failed (' + r.status + ')');
      return r.json();
    });
  }

  /* ── Feedback display ──────────────────────────── */

  function getInlineFeedback(container) {
    if (!container) return null;
    return container.querySelector(FEEDBACK_SEL);
  }

  function setFeedbackNode(el, message, type, visible) {
    if (!el) return;
    var kind = type || 'info';
    el.textContent = message || '';
    el.className = 'dd-discount__feedback dd-discount__feedback--' + kind +
      (visible ? ' is--visible' : '');
  }

  /* Like setFeedbackNode but accepts pre-built HTML for messages that
     embed a CTA (e.g., the gift-card "Apply at checkout" handoff).
     Caller is responsible for escaping any user-supplied content
     (use escHtml). */
  function setFeedbackHTML(el, html, type, visible) {
    if (!el) return;
    var kind = type || 'info';
    el.innerHTML = html || '';
    el.className = 'dd-discount__feedback dd-discount__feedback--' + kind +
      (visible ? ' is--visible' : '');
  }

  function showFeedback(container, message, type) {
    var el = getInlineFeedback(container);
    if (el) {
      /* Always clear any pending hide timer so a 'loading' feedback set
         while a prior 'success'/'warn' timer is still in flight isn't
         clobbered mid-await. The setTimeout below only re-arms for
         non-loading types. */
      clearTimeout(el._hideTimer);
      setFeedbackNode(el, message, type, true);
      if (type !== 'loading') {
        el._hideTimer = setTimeout(function () {
          setFeedbackNode(el, '', 'info', false);
        }, 4000);
      }
    }
  }

  /* Persistent (no auto-hide) feedback used for actionable handoffs.
     The user needs time to read and click the embedded link, so we
     don't run the 4-second timer here.

     The href uses Shopify's discount deeplink format
     (`/discount/CODE?redirect=/checkout`) which auto-applies the
     code on entering checkout — much less friction than the legacy
     bare `/checkout` link, which dropped the shopper at checkout
     with an empty discount field they'd have to re-type. The
     redirect target keeps the existing post-apply destination
     intact so checkout continues in the expected place.

     `encodeURIComponent` on rawCode handles legal-but-rare code
     characters (slashes, ampersands, plus signs) that would break
     the URL path otherwise. The CSP/router will still reject codes
     with truly malformed paths, but those wouldn't have been
     valid Shopify codes to begin with. */
  function showCheckoutHandoff(container, rawCode) {
    var el = getInlineFeedback(container);
    if (!el) return;
    clearTimeout(el._hideTimer);
    var safeCode = escHtml(rawCode);
    var deeplink = '/discount/' + encodeURIComponent(rawCode) + '?redirect=/checkout';
    var html =
      '<span>We couldn\u2019t apply <strong>' + safeCode + '</strong> here. ' +
      'Gift cards apply at checkout.</span> ' +
      '<a href="' + escAttr(deeplink) + '" class="dd-discount__feedback-action" ' +
      'data-dd-discount-checkout-handoff>Apply at checkout \u2192</a>';
    setFeedbackHTML(el, html, 'warn', true);
  }

  function clearFeedback(container) {
    var el = getInlineFeedback(container);
    if (!el) return;
    clearTimeout(el._hideTimer);
    setFeedbackNode(el, '', 'info', false);
  }

  /* Inspect the cart response for the verdict on a specific code.
     Shopify's /cart/update.js response includes `discount_codes`:
       [{ code: "SAVE10", applicable: true }, ...]
     This is more reliable than checking DOM badges (which depend on
     section re-render timing) or total_discount (which doesn't change
     for free-shipping or shipping-only codes that are still validly
     attached).

     Return shape:
       { applicable: bool }  -- code present in response
       null                  -- code NOT present (Shopify didn't accept it
                                as a discount code at all; typical for
                                gift cards or typos)
       undefined             -- response doesn't include discount_codes
                                (older API / unexpected shape) → caller
                                should fall back to legacy badge check */
  function findDiscountStatus(data, code) {
    if (!data || !code) return undefined;
    var arr = data.discount_codes;
    if (!Array.isArray(arr)) return undefined;
    var norm = normalizeCode(code);
    for (var i = 0; i < arr.length; i++) {
      var entry = arr[i];
      if (!entry) continue;
      if (normalizeCode(entry.code) === norm) {
        return { applicable: !!entry.applicable };
      }
    }
    return null;
  }

  /* ── Chip rendering (tag input) ───────────────── */

  var CHIP_ICON_SVG = '<svg viewBox="0 0 448 512" width="12" height="12" aria-hidden="true"><path d="M48 32H197.5C214.5 32 230.7 38.74 242.7 50.75L418.7 226.7C443.7 251.7 443.7 292.3 418.7 317.3L285.3 450.7C260.3 475.7 219.7 475.7 194.7 450.7L18.75 274.7C6.743 262.7 0 246.5 0 229.5V80C0 53.49 21.49 32 48 32zM112 176C129.7 176 144 161.7 144 144C144 126.3 129.7 112 112 112C94.33 112 80 126.3 80 144C80 161.7 94.33 176 112 176z" fill="currentColor"/></svg>';
  var CHIP_REMOVE_SVG = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function renderChips() {
    var hosts = document.querySelectorAll(CHIP_HOST_SEL);
    if (!hosts.length) return;

    var allBadges = document.querySelectorAll('[data-dd-discount-badge]');
    var appliedCodes = [];
    var autoDiscounts = [];
    var appliedNorm = {};
    var seenAuto = {};
    var activatedFromPending = [];
    for (var b = 0; b < allBadges.length; b++) {
      var badge = allBadges[b];
      var bType = (badge.getAttribute('data-discount-type') || '').trim();
      var bCode = (badge.getAttribute('data-discount-code') || '').trim();
      if (!bCode) continue;
      if (bType === 'discount_code') {
        var norm = normalizeCode(bCode);
        if (appliedNorm[norm]) continue;
        appliedNorm[norm] = true;
        appliedCodes.push(bCode);
        /* Activation detection — if this code was previously applied
           with `applicable: false` (added to _pendingCodes by the
           apply path) and a real badge has now materialized (the
           shopper's cart change satisfied the unmet condition,
           Shopify auto-flipped applicable → true), surface a positive
           "Discount activated" message at the end of this render so
           the shopper sees a clear resolution to the earlier "not
           active yet" feedback. One-shot — delete from the set so we
           don't re-emit on every subsequent renderChips call. */
        if (_pendingCodes[norm]) {
          activatedFromPending.push(_pendingCodes[norm]);
          deletePendingCode(norm);
        }
      } else {
        var autoKey = bCode.toLowerCase();
        if (seenAuto[autoKey]) continue;
        seenAuto[autoKey] = true;
        var spanEl = badge.querySelector('span');
        autoDiscounts.push({ code: bCode, label: spanEl ? spanEl.textContent.trim() : bCode });
      }
    }

    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      host.innerHTML = '';

      for (var ad = 0; ad < autoDiscounts.length; ad++) {
        var autoChip = document.createElement('div');
        autoChip.className = 'dd-cart-discount dd-discount-chip dd-cart-discount--auto';
        autoChip.innerHTML =
          CHIP_ICON_SVG +
          '<span>' + escHtml(autoDiscounts[ad].label) + '</span>';
        host.appendChild(autoChip);
      }

      for (var a = 0; a < appliedCodes.length; a++) {
        var validChip = document.createElement('div');
        validChip.className = 'dd-cart-discount dd-discount-chip';
        validChip.setAttribute('data-dd-discount-chip', '');
        validChip.setAttribute('data-discount-code', appliedCodes[a]);
        validChip.innerHTML =
          CHIP_ICON_SVG +
          '<span>' + escHtml(appliedCodes[a]) + '</span>' +
          '<button type="button" class="dd-cart-discount__remove" aria-label="Remove discount ' +
            escAttr(appliedCodes[a]) + '">' + CHIP_REMOVE_SVG + '</button>';
        host.appendChild(validChip);
      }

      /* Pending chips — visually distinct from applied (no
         brand-tag fill) and from failed (no error styling) because
         the state is "Shopify is holding this for you, conditions
         aren't met yet". Shopper can click the X to actively remove
         the pending code from cart.discount_codes server-side
         (handled in the click delegate via `data-dd-pending-chip`).
         Skip rendering if a real applied badge already exists for
         the same code — covers the brief render-cycle gap between
         renderChips() detecting activation and `_pendingCodes`
         being cleared by deletePendingCode (defense-in-depth). */
      var pkeys = Object.keys(_pendingCodes);
      for (var pk = 0; pk < pkeys.length; pk++) {
        var pkey = pkeys[pk];
        if (appliedNorm[pkey]) continue;
        var pendingCode = _pendingCodes[pkey];
        if (!pendingCode) continue;
        var pendingChip = document.createElement('div');
        pendingChip.className = 'dd-cart-discount dd-discount-chip dd-cart-discount--pending';
        pendingChip.setAttribute('data-dd-pending-chip', pendingCode);
        pendingChip.setAttribute('data-discount-code', pendingCode);
        pendingChip.setAttribute('title', 'Code accepted, not active yet — will apply automatically when conditions are met');
        pendingChip.innerHTML =
          CHIP_ICON_SVG +
          '<span>' + escHtml(pendingCode) + '</span>' +
          '<span class="dd-discount-chip__pending-label" aria-hidden="true">pending</span>' +
          '<button type="button" class="dd-cart-discount__remove" aria-label="Remove pending code ' +
            escAttr(pendingCode) + '">' + CHIP_REMOVE_SVG + '</button>';
        host.appendChild(pendingChip);
      }

      for (var i = 0; i < _failedCodes.length; i++) {
        var fc = _failedCodes[i];
        if (appliedNorm[normalizeCode(fc.code)]) continue;
        var failChip = document.createElement('div');
        failChip.className = 'dd-cart-discount dd-discount-chip dd-cart-discount--invalid';
        failChip.setAttribute('data-dd-failed-chip', fc.code);
        failChip.setAttribute('data-discount-code', fc.code);
        failChip.innerHTML =
          '<span>' + escHtml(fc.code) + '</span>' +
          '<button type="button" class="dd-cart-discount__remove" aria-label="Dismiss ' +
            escAttr(fc.code) + '">' + CHIP_REMOVE_SVG + '</button>';
        host.appendChild(failChip);
      }
    }

    /* Pending → activated handoff. We emit one message per render
       regardless of how many pending codes flipped (rare to have more
       than one anyway), and use the freshest discount-input container
       since that's where the original "not active yet" feedback was
       shown — the shopper's eyes are already trained there. Also
       fire a global toast so the activation is visible even if the
       cart drawer/page is closed when the badge materializes (e.g.,
       a /cart/add.js from a PDP that doesn't auto-open the drawer). */
    if (activatedFromPending.length) {
      var listLabel = activatedFromPending
        .map(function (c) { return String(c || '').toUpperCase(); })
        .join(', ');
      var activationMsg = activatedFromPending.length === 1
        ? 'Discount activated — ' + listLabel + ' is now applied.'
        : 'Discounts activated — ' + listLabel + ' are now applied.';
      var feedbackContainer = document.querySelector(CONTAINER_SEL);
      if (feedbackContainer) {
        showFeedback(feedbackContainer, activationMsg, 'success');
      }
      if (typeof window.ddToast === 'function') {
        try { window.ddToast(activationMsg, { type: 'success' }); } catch (_e) { /* swallow */ }
      }
    }
  }

  function escHtml(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escAttr(s) {
    return escHtml(s).replace(/"/g, '&quot;');
  }

  function addFailedChip(code) {
    var norm = normalizeCode(code);
    for (var i = _failedCodes.length - 1; i >= 0; i--) {
      if (normalizeCode(_failedCodes[i].code) === norm) _failedCodes.splice(i, 1);
    }
    var entry = { code: code.trim(), timer: null };
    _failedCodes.push(entry);
    renderChips();
    entry.timer = setTimeout(function () {
      removeFailedChip(code);
    }, CHIP_DISMISS_MS);
  }

  function removeFailedChip(code) {
    var norm = normalizeCode(code);
    for (var i = _failedCodes.length - 1; i >= 0; i--) {
      if (normalizeCode(_failedCodes[i].code) === norm) {
        clearTimeout(_failedCodes[i].timer);
        _failedCodes.splice(i, 1);
      }
    }
    renderChips();
  }

  function clearFailedChipByCode(code) {
    var norm = normalizeCode(code);
    for (var i = _failedCodes.length - 1; i >= 0; i--) {
      if (normalizeCode(_failedCodes[i].code) === norm) {
        clearTimeout(_failedCodes[i].timer);
        _failedCodes.splice(i, 1);
      }
    }
  }

  /* ── Apply discount ────────────────────────────── */

  function normalizeCode(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function adjustedTotalDiscountCents(cart) {
    if (!cart) return 0;
    var total = Number(cart.total_discount || 0);
    if (!isFinite(total) || total < 0) total = 0;
    return total;
  }

  function getDiscountContextRoot(fromEl) {
    if (!fromEl || !fromEl.closest) return document;
    var drawerRoot = fromEl.closest('cart-drawer');
    if (drawerRoot) return drawerRoot;
    var cartFooter = fromEl.closest('#main-cart-footer');
    if (cartFooter) return cartFooter;
    return document;
  }

  function getScopedNode(root, selector) {
    if (root && typeof root.querySelector === 'function') {
      var scoped = root.querySelector(selector);
      if (scoped) return scoped;
    }
    return document.querySelector(selector);
  }

  function getScopedNodes(root, selector) {
    if (root && typeof root.querySelectorAll === 'function') {
      var scoped = root.querySelectorAll(selector);
      if (scoped && scoped.length) return scoped;
    }
    return document.querySelectorAll(selector);
  }

  function nextDiscountOpToken() {
    _discountOpToken += 1;
    return _discountOpToken;
  }

  function isLatestDiscountOp(token) {
    return _discountOpToken === token;
  }

  function hasActiveDiscountCode(normalizedCode, contextRoot) {
    if (!normalizedCode) return false;
    var badges = getScopedNodes(contextRoot, '[data-dd-discount-badge]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (badge.getAttribute('data-discount-type') !== 'discount_code') continue;
      var badgeCode = normalizeCode(badge.getAttribute('data-discount-code'));
      if (badgeCode === normalizedCode) return true;
    }
    return false;
  }

  /**
   * Build the full list of codes currently attached to the cart, used to
   * compose the `discount` param for /cart/update.js — which REPLACES all
   * server codes wholesale. An incomplete list silently drops valid codes,
   * so sourcing matters:
   *
   *   1. Server truth first: the cart queue caches the authoritative cart
   *      after every mutation (markCartFresh), and `cart.discount_codes`
   *      includes codes regardless of which surface rendered them AND
   *      pending (applicable:false) codes Shopify is holding. No fetch —
   *      it's a cache read.
   *   2. DOM fallback (no cached cart yet, e.g. first interaction after a
   *      hard load): read badges DOCUMENT-WIDE, not scoped to one surface.
   *      On /cart both the drawer and the page footer render badges; a
   *      single-surface read missed codes that only the other surface had
   *      (stale drawer) and wiped them on the next apply/remove. SSR badges
   *      on a fresh load reflect server state, so the union is correct.
   *
   * Note: server sourcing also PRESERVES pending codes on unrelated
   * apply/remove ops (previously they were silently dropped because they
   * have no badge). The targeted code — active or pending — is still
   * excluded by the filter in discountParamAfterRemovingCode.
   */
  function existingCodeDiscounts() {
    var helpers = window.ddCartHelpers || {};
    var cart = typeof helpers.getLatestCart === 'function' ? helpers.getLatestCart() : null;
    var codes = [];
    var seen = {};

    if (cart && Array.isArray(cart.discount_codes)) {
      for (var c = 0; c < cart.discount_codes.length; c++) {
        var entry = cart.discount_codes[c];
        var entryCode = entry && entry.code ? String(entry.code).trim() : '';
        if (!entryCode) continue;
        var entryNorm = normalizeCode(entryCode);
        if (seen[entryNorm]) continue;
        seen[entryNorm] = true;
        codes.push(entryCode);
      }
      return codes;
    }

    var badges = document.querySelectorAll('[data-dd-discount-badge]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (badge.getAttribute('data-discount-type') !== 'discount_code') continue;
      var title = (badge.getAttribute('data-discount-code') || '').trim();
      if (!title) continue;
      var norm = normalizeCode(title);
      if (seen[norm]) continue;
      seen[norm] = true;
      codes.push(title);
    }
    return codes;
  }

  function discountParamAfterRemovingCode(codeToRemove) {
    var target = normalizeCode(codeToRemove);
    var codes = existingCodeDiscounts();
    var keep = [];
    for (var i = 0; i < codes.length; i++) {
      if (target && normalizeCode(codes[i]) === target) continue;
      keep.push(codes[i]);
    }
    return keep.join(',');
  }

  function discountParamAfterAddingCode(newCode) {
    var codes = existingCodeDiscounts();
    var norm = normalizeCode(newCode);
    for (var i = 0; i < codes.length; i++) {
      if (normalizeCode(codes[i]) === norm) return codes.join(',');
    }
    codes.push(newCode.trim());
    return codes.join(',');
  }

  function applyCode(code, container) {
    if (_isApplying) {
      showFeedback(container, 'Cart is updating\u2026', 'info');
      return;
    }
    var rawCode = code.trim();
    var normalizedCode = normalizeCode(rawCode);
    if (!rawCode) {
      showFeedback(container, 'Enter a code first.', 'warn');
      var emptyInput = container.querySelector(INPUT_SEL);
      if (emptyInput) emptyInput.focus();
      return;
    }
    var contextRoot = getDiscountContextRoot(container);
    if (hasActiveDiscountCode(normalizedCode, contextRoot)) {
      showFeedback(container, 'This discount is already applied', 'info');
      return;
    }

    _isApplying = true;
    startApplyingTimeout();
    var opToken = nextDiscountOpToken();
    container.classList.add('is--applying');
    var applyBtn = container.querySelector(APPLY_SEL);
    if (applyBtn) {
      applyBtn.dataset.originalText = applyBtn.textContent;
      applyBtn.textContent = 'Applying…';
      applyBtn.disabled = true;
    }
    clearFeedback(container);
    showFeedback(container, 'Applying\u2026', 'loading');
    /* Optimistically persist the in-flight code so a mid-apply page
       reload (slow connection, accidental refresh, browser crash)
       doesn't lose what the shopper just typed. The companion cleanup
       in the success/rejected branches below clears localStorage as
       soon as the request resolves, so this entry only lives long
       enough to power the recovery path. */
    try { localStorage.setItem('CartDiscountcode', rawCode); } catch (e) {}

    /* Cleanup helper run from the AJAX chain's `.finally`. Resets the
       apply lock + button text. */
    function runApplyCleanup() {
      if (!isLatestDiscountOp(opToken)) return;
      _isApplying = false;
      clearApplyingTimeout();
      var containers = document.querySelectorAll(CONTAINER_SEL);
      containers.forEach(function (c) {
        c.classList.remove('is--applying');
        var btn = c.querySelector(APPLY_SEL);
        if (btn) {
          btn.disabled = false;
          btn.textContent = btn.dataset.originalText || 'Apply';
        }
      });
    }

    /* Discount-first routing. This store issues BOTH discount codes and
       gift cards as 12-hex strings (e.g. "9cc191be6724"), so a code's
       shape cannot tell them apart. We always try the AJAX cart discount
       path first; genuine gift cards fall through to the Storefront API
       gift-card fallback below (the applicable:false / status===null
       branch in the .then handler).

       A previous "looks like a gift card → Storefront fast-path"
       optimization mis-routed hex-shaped DISCOUNT codes to
       cartGiftCardCodesAdd. That mutation rejects a discount code (it
       isn't a gift card), and when the Storefront call itself failed
       (e.g. a 401 from an invalid storefront token) it surfaced a
       misleading "connection issue" toast — so a valid discount code
       could never be applied. Removing the fast-path costs at most one
       extra request for genuine gift cards (the discount preflight),
       which is within the per-action budget. */

    var summaryEl = getScopedNode(contextRoot, '[data-dd-total-discount]');
    var preDiscount = summaryEl ? parseInt(summaryEl.getAttribute('data-dd-total-discount') || '0', 10) : 0;
    if (isNaN(preDiscount)) preDiscount = 0;

    /* Build combined discount param: existing codes + new code (comma-separated).
       Shopify /cart/update.js `discount` param REPLACES all codes, so we must
       re-send the ones we want to keep. */
    var combinedDiscount = discountParamAfterAddingCode(rawCode);

    mutateDiscount({ discount: combinedDiscount }, 'discount-apply')
      .then(function (data) {
        if (!isLatestDiscountOp(opToken)) return;
        var freshContainer = getScopedNode(contextRoot, CONTAINER_SEL);
        if (!freshContainer) freshContainer = container;

        /* Source of truth for whether the code applied:
           1. data.discount_codes[].applicable when the API returns it
              (covers free-shipping and other codes that don't bump
              total_discount but are still validly attached).
           2. Fall back to DOM badges + total_discount delta if the
              response didn't include discount_codes for some reason. */
        var status = findDiscountStatus(data, rawCode);
        var badges = getScopedNodes(contextRoot, '[data-dd-discount-badge]');
        var legacyApplied = false;
        if (status === undefined) {
          for (var li = 0; li < badges.length; li++) {
            if (normalizeCode(badges[li].getAttribute('data-discount-code')) === normalizedCode) {
              legacyApplied = true;
              break;
            }
          }
          if (!legacyApplied && adjustedTotalDiscountCents(data) > preDiscount) {
            legacyApplied = true;
          }
        }
        var didApply = (status && status.applicable === true) || (status === undefined && legacyApplied);

        if (didApply) {
          /* Successful apply: the chip on screen is now the source of
             truth for "what discount code is active." Clear the saved
             value so the next page load doesn't re-prefill an input
             that already has its corresponding chip rendered. */
          try { localStorage.removeItem('CartDiscountcode'); } catch (e) {}
          clearFailedChipByCode(rawCode);
          showFeedback(freshContainer, 'Discount applied!', 'success');
          var input = freshContainer.querySelector(INPUT_SEL);
          if (input) input.value = '';
          renderChips();
          if (window.ddCartHelpers && typeof window.ddCartHelpers.scheduleCloseCartAccordions === 'function') {
            window.ddCartHelpers.scheduleCloseCartAccordions(freshContainer);
          }
          return;
        }

        /* All rejection paths: clear the optimistic save so the
           rejected code can't resurface as a prefill on next load. */
        try { localStorage.removeItem('CartDiscountcode'); } catch (e) {}

        /* "A better discount is already active" — fires when there's a
           DIFFERENT discount-code badge present and Shopify didn't add
           ours. This is most common on stores that auto-replace one
           code with another based on best-deal logic. */
        var hasOtherCode = false;
        for (var j = 0; j < badges.length; j++) {
          var badge = badges[j];
          if (badge.getAttribute('data-discount-type') !== 'discount_code') continue;
          if (normalizeCode(badge.getAttribute('data-discount-code')) !== normalizedCode) {
            hasOtherCode = true;
            break;
          }
        }

        /* `_isPendingThisOp` is set to true by runDiscountFallback when
           it detects "code accepted, condition unmet" (the
           applicable:false-no-conflict sub-case). Read by the
           addFailedChip gate below to skip rendering a failed-chip in
           that case — a failed chip implies the code was rejected,
           but a pending code is actually attached to the cart
           server-side and may auto-activate later (e.g., when the
           shopper adds the qualifying product). Showing a "rejected"
           chip with a dismiss-X would be misleading: the shopper
           thinks they cleared a code that's still on the cart, then
           sees the discount apply moments later when conditions are
           met and wonders if the toast was wrong (the original UX
           bug this guard fixes).

           Hoisted to function scope here (rather than inside the
           else-branch where runDiscountFallback is defined) because
           the trailing `addFailedChip(rawCode)` at the end of this
           handler also runs when `hasOtherCode` is true, and that
           branch must keep its current "failed chip" behavior — the
           code was genuinely rejected for combinability with another
           code, not pending. The variable defaults to false so the
           hasOtherCode branch behaves identically to before. */
        var _isPendingThisOp = false;

        /* Gift cards and discount codes are indistinguishable by shape on
           this store (both 12-hex), so we must ALWAYS try the gift-card path
           on rejection — even when another discount code is already active.
           Gift cards are stored value, NOT discounts, and stack with
           everything. The previous `if (hasOtherCode)` short-circuit showed
           "a better discount is already active" and never attempted the gift
           card, so a gift card could never be redeemed while another code sat
           on the cart. We now attempt the gift card first; only if that fails
           do we fall back to the combinability message (gated on hasOtherCode
           at the top of runDiscountFallback). The bare block below preserves
           the original trailing brace. */
        {
          /* Two sub-cases reach this branch and BOTH need the gift-card
             attempt before we show a discount-rejection message:

             A) `status.applicable === false` — Shopify recognized the
                code as a real discount code and rejected it. Common
                causes: combinability with an existing auto-discount,
                expiry, single-use already redeemed, customer
                restriction, cart-minimum unmet. SOME stores ALSO route
                gift card codes through here with applicable:false
                instead of leaving them out of discount_codes — this
                store does (observed with Smile-issued gift cards).
             B) `status === null` — code wasn't in discount_codes at
                all. Typically a gift card or a typo.

             In either case, the right move is: try cartGiftCardCodesAdd
             first (cheap — one Storefront request), and only show the
             discount-rejection message if THAT also fails. The
             rejection message itself differs per sub-case:

               A) auto-badge present  → "can't combine with X discount"
                  (most common when the cart has a customer-tier auto)
               A) no auto-badge       → "expired/used/restricted" generic
               B)                     → "Apply at checkout" handoff (the
                  legacy code path that has historically worked for gift
                  cards Shopify's APIs can't apply in-cart) */

          var runDiscountFallback = function (fbContainer) {
            /* Reached only after the gift-card attempt failed (or SF is
               off). If another discount code is already active, the entered
               code is a genuine discount code that can't combine — show the
               "better discount" message instead of the auto-badge combine
               copy or the gift-card handoff. */
            if (hasOtherCode) {
              showFeedback(fbContainer, 'A better discount is already active on your cart.', 'info');
              return;
            }
            if (status && status.applicable === false) {
              var autoBadge = null;
              for (var k = 0; k < badges.length; k++) {
                var bType = badges[k].getAttribute('data-discount-type');
                if (bType && bType !== 'discount_code') {
                  autoBadge = badges[k];
                  break;
                }
              }
              if (autoBadge) {
                var autoSpan = autoBadge.querySelector('span');
                var autoNameRaw = autoSpan
                  ? autoSpan.textContent
                  : (autoBadge.getAttribute('data-discount-code') || '');
                /* Auto badge labels often include the savings amount in
                   parens (e.g. "Inner Circle (-$130)"). Strip that for
                   a cleaner sentence — the parenthetical isn't useful
                   in this context, the chip above already shows it. */
                var autoName = String(autoNameRaw || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
                if (!autoName) autoName = 'automatic';
                showFeedback(
                  fbContainer,
                  'This code can\u2019t be combined with your ' + autoName + ' discount.',
                  'warn'
                );
              } else {
                /* applicable:false WITHOUT an auto-badge conflict.
                   Shopify recognized the code (it's in
                   data.discount_codes) but won't activate it yet.
                   The cause is ambiguous from /cart/update.js's
                   response — could be expired/already-used (truly
                   failed), or a product-tied / minimum-order /
                   customer-eligibility condition that's currently
                   unmet (pending — will auto-activate on the next
                   /cart/add.js or /cart/change.js once conditions
                   are met, since those endpoints don't replace
                   discount_codes the way /cart/update.js with a
                   `discount` param does).

                   We can't tell those two sub-cases apart, so frame
                   the message in a way that's accurate for BOTH.
                   The previous copy ("expired, already used, or
                   restricted") was authoritative-failure framing,
                   which contradicts the actual UX when a pending
                   code activates moments later (e.g., shopper adds
                   a qualifying product after entering the code) —
                   the shopper sees the discount apply in the cart
                   but remembers a "didn't work" toast. New copy
                   matches reality on both branches without lying.

                   Mark `_isPendingThisOp` and seed `_pendingCodes`
                   so renderChips() can detect the activation event
                   later and emit a positive "Discount activated"
                   message to close the loop. */
                showFeedback(
                  fbContainer,
                  'Code accepted but not active yet \u2014 your cart may need a qualifying product, larger order, or other condition. It\u2019ll apply automatically once met.',
                  'info'
                );
                _isPendingThisOp = true;
                setPendingCode(normalizedCode, rawCode);
              }
            } else {
              /* status === null — gift card or typo. Hand off to
                 checkout where Shopify's checkout-side gift card
                 path can still try (that path has more privileged
                 access than the Storefront API for some stores). */
              showCheckoutHandoff(fbContainer, rawCode);
            }
          };

          var sfReady = window.ddSFCart && typeof window.ddSFCart.isConfigured === 'function' && window.ddSFCart.isConfigured();
          if (sfReady) {
            showFeedback(freshContainer, 'Checking gift card\u2026', 'loading');
            window.ddSFCart.apply(rawCode, { cartToken: data && data.token })
              .then(function (gcResult) {
                if (!isLatestDiscountOp(opToken)) return;
                var gcContainer = getScopedNode(contextRoot, CONTAINER_SEL) || freshContainer;
                if (gcResult && gcResult.ok) {
                  /* On this Plus store, /cart.js and Liquid are blind
                     to SF-applied gift cards (verified empirically:
                     SF query() sees 1 applied, /cart.js shows 0).
                     The section-render painter therefore can't display
                     the chip — it copies empty markup over the chip
                     slot. We paint client-side from the SF API result
                     `gcResult` (which already has the cart state with
                     applied + cost), then run the section-refresh in
                     parallel so other cart UI (line items, savings,
                     etc.) stays current. */
                  if (window.ddSFCart && typeof window.ddSFCart.hydrate === 'function') {
                    window.ddSFCart.hydrate({ state: gcResult });
                  }
                  return refreshCartSectionsAfterGiftCard().then(function () {
                    if (!isLatestDiscountOp(opToken)) return;
                    /* Section render wiped our chip + reverted the
                       subtotal — re-hydrate to restore. */
                    if (window.ddSFCart && typeof window.ddSFCart.hydrate === 'function') {
                      window.ddSFCart.hydrate({ state: gcResult });
                    }
                    var afterContainer = getScopedNode(contextRoot, CONTAINER_SEL) || gcContainer;
                    try { localStorage.removeItem('CartDiscountcode'); } catch (e) {}
                    clearFailedChipByCode(rawCode);
                    showFeedback(afterContainer, 'Gift card applied!', 'success');
                    var sInput = afterContainer.querySelector(INPUT_SEL);
                    if (sInput) sInput.value = '';
                    if (window.ddCartHelpers && typeof window.ddCartHelpers.scheduleCloseCartAccordions === 'function') {
                      window.ddCartHelpers.scheduleCloseCartAccordions(afterContainer);
                    }
                  });
                }
                /* SF API rejected the code — show the sub-case-aware
                   discount-rejection message. Skip the failed-chip if
                   runDiscountFallback flagged the op as pending (the
                   code is attached to the cart server-side and will
                   activate when conditions are met — a "rejected"
                   chip with a dismiss-X would mislead the shopper
                   into thinking they cleared a code that's still
                   applied). */
                runDiscountFallback(gcContainer);
                if (!_isPendingThisOp) addFailedChip(rawCode);
                var fInput = gcContainer.querySelector(INPUT_SEL);
                if (fInput) fInput.value = '';
              })
              .catch(function (sfErr) {
                if (!isLatestDiscountOp(opToken)) return;
                console.error('[DiscountCode] gift card apply error', sfErr);
                var errContainer = getScopedNode(contextRoot, CONTAINER_SEL) || freshContainer;
                runDiscountFallback(errContainer);
                if (!_isPendingThisOp) addFailedChip(rawCode);
                var eInput = errContainer.querySelector(INPUT_SEL);
                if (eInput) eInput.value = '';
              });
            return;
          }

          /* SF API not configured (kill switch off / no token) — go
             straight to the sub-case-aware discount-rejection message
             without an extra network call. */
          runDiscountFallback(freshContainer);
        }
        if (!_isPendingThisOp) addFailedChip(rawCode);
        var failInput = freshContainer.querySelector(INPUT_SEL);
        if (failInput) failInput.value = '';
      })
      .catch(function (err) {
        if (!isLatestDiscountOp(opToken)) return;
        console.error('[DiscountCode] apply error', err);
        /* Network/transport failure (NOT a server-rejection — that
           goes through the success branch above with `codeApplied:
           false`). We INTENTIONALLY do NOT clear localStorage here:
           the optimistic save at apply time is still useful as a
           recovery breadcrumb, so a refresh-and-retry can repopulate
           the input and the shopper can hit Apply again. The previous
           copy here ("your code was saved and will apply at checkout")
           was misleading — Shopify checkout does not read our
           localStorage, so the code only "applies" if the user
           manually retries. New copy reflects that honestly. */
        showFeedback(container, 'Connection issue \u2014 please try again.', 'warn');
      })
      .finally(runApplyCleanup);
  }

  /* ── Remove discount ──────────────────────────── */

  function removeCode(displayCode) {
    if (_isApplying) {
      if (window.ddToast) window.ddToast('Cart is updating\u2026', { type: 'info' });
      return;
    }
    _isApplying = true;
    startApplyingTimeout();
    var opToken = nextDiscountOpToken();

    var normalizedDisplayCode = normalizeCode(displayCode);
    /* Two valid removal targets exist:
         1. An active code (has a `[data-dd-discount-badge]` on either
            surface — checked document-wide, since on /cart the drawer
            and the page footer both render badges).
         2. A pending code (in `_pendingCodes`, NO badge because
            applicable:false doesn't render in
            cart.cart_level_discount_applications). The shopper sees
            it as a `--pending` chip and clicks X to actively remove
            it from `cart.discount_codes` server-side.
       Bail only if the code is in NEITHER state — otherwise the
       mutation below will correctly drop the code (whether active
       or pending) by re-sending the kept-codes list. */
    var isPendingTarget = normalizedDisplayCode && _pendingCodes[normalizedDisplayCode];
    if (normalizedDisplayCode && !hasActiveDiscountCode(normalizedDisplayCode) && !isPendingTarget) {
      if (isLatestDiscountOp(opToken)) _isApplying = false;
      return;
    }
    /* nextDiscount = the kept-codes list (server cart cache first, DOM
       union fallback — see existingCodeDiscounts) minus the target.
       Submitting it to /cart/update.js REPLACES all server codes, so
       the target (active or pending) is dropped while every other
       code — including unrelated pending codes — is preserved. */
    var nextDiscount = discountParamAfterRemovingCode(displayCode);
    if (!normalizedDisplayCode && !nextDiscount && !isPendingTarget) {
      if (isLatestDiscountOp(opToken)) _isApplying = false;
      return;
    }

    /* Optimistic UI: the chip/badge disappears immediately, but we keep an
       undo handle. If the mutation FAILS, the server still has the code —
       restoring the nodes (and the pending bookkeeping) keeps the UI
       truthful instead of showing a discount as removed when checkout
       will still apply it. */
    var undoOptimistic = optimisticRemoveChipAndBadge(normalizedDisplayCode);
    var pendingRawCode = isPendingTarget ? _pendingCodes[normalizedDisplayCode] : null;
    if (isPendingTarget) deletePendingCode(normalizedDisplayCode);

    mutateDiscount({ discount: nextDiscount }, 'discount-remove')
      .then(function (data) {
        if (!isLatestDiscountOp(opToken)) return;
        try { localStorage.removeItem('CartDiscountcode'); } catch (e) {}
        if (window.ddToast) {
          var msg = displayCode ? ('Discount ' + displayCode + ' removed') : 'Discount removed';
          window.ddToast(msg, { type: 'info' });
        }
        renderChips();
      })
      .catch(function (err) {
        if (!isLatestDiscountOp(opToken)) return;
        console.error('[DiscountCode] remove error', err);
        /* Roll back the optimistic removal — server state is unchanged. */
        try { undoOptimistic(); } catch (undoErr) { console.error('[DiscountCode] rollback error', undoErr); }
        if (pendingRawCode) setPendingCode(normalizedDisplayCode, pendingRawCode);
        if (window.ddToast) window.ddToast('Couldn\u2019t remove discount \u2014 please try again', { type: 'error' });
        renderChips();
      })
      .finally(function () {
        if (!isLatestDiscountOp(opToken)) return;
        _isApplying = false;
        clearApplyingTimeout();
      });
  }

  /* Removes matching chips + badges document-wide and returns an undo
     function that re-inserts every removed node at its original position
     (parent + nextSibling captured before removal). Used by removeCode's
     failure path to roll back the optimistic UI. */
  function optimisticRemoveChipAndBadge(normalizedCode) {
    var removed = [];
    if (!normalizedCode) return function () {};
    function removeMatching(selector) {
      var nodes = document.querySelectorAll(selector);
      for (var i = nodes.length - 1; i >= 0; i--) {
        if (normalizeCode(nodes[i].getAttribute('data-discount-code')) === normalizedCode) {
          removed.push({ node: nodes[i], parent: nodes[i].parentNode, next: nodes[i].nextSibling });
          nodes[i].remove();
        }
      }
    }
    removeMatching('[data-dd-discount-chip]');
    removeMatching('[data-dd-discount-badge]');
    return function undo() {
      for (var i = 0; i < removed.length; i++) {
        var r = removed[i];
        if (!r.parent || !r.parent.isConnected || r.node.isConnected) continue;
        if (r.next && r.next.parentNode === r.parent) r.parent.insertBefore(r.node, r.next);
        else r.parent.appendChild(r.node);
      }
    };
  }

  /* ── Event delegation ──────────────────────────── */

  document.addEventListener('click', function (e) {
    var clickTarget = e && e.target;
    if (clickTarget && clickTarget.nodeType === 3) clickTarget = clickTarget.parentElement;
    if (!clickTarget || typeof clickTarget.closest !== 'function') return;
    /* Remove chip inside composite input (failed, pending, or valid) */
    var chipRemoveBtn = clickTarget.closest('.dd-discount-chip .dd-cart-discount__remove');
    if (chipRemoveBtn) {
      e.preventDefault();
      var failedChip = chipRemoveBtn.closest('[data-dd-failed-chip]');
      if (failedChip) {
        removeFailedChip(failedChip.getAttribute('data-discount-code') || '');
        return;
      }
      /* Pending chip removal — routes through the same removeCode
         flow as active chips. removeCode is now pending-aware and
         will issue the discount-replace mutation that drops the
         pending code from cart.discount_codes server-side. Order
         matters: check pending BEFORE the generic active-chip
         branch because the pending chip ALSO carries
         `data-dd-discount-chip` is NOT present (it has
         `data-dd-pending-chip` instead), so the active branch
         wouldn't match anyway — but we keep this branch explicit
         for clarity and future-proofing if attribute conventions
         change. */
      var pendingChipEl = chipRemoveBtn.closest('[data-dd-pending-chip]');
      if (pendingChipEl) {
        var pendingChipCode = pendingChipEl.getAttribute('data-discount-code') || '';
        removeCode(pendingChipCode);
        return;
      }
      var validChip = chipRemoveBtn.closest('[data-dd-discount-chip]');
      if (validChip) {
        var chipCode = validChip.getAttribute('data-discount-code') || '';
        removeCode(chipCode);
        return;
      }
    }

    /* Remove applied gift card (Storefront API) — must come BEFORE
       the generic `.dd-cart-discount__remove` branch below, because
       the gift-card badge intentionally reuses that class for visual
       parity but is not a `[data-dd-discount-badge]`. Without this
       branch the generic handler would call `removeCode('', ...)` and
       quietly do nothing. */
    var giftRemoveBtn = clickTarget.closest('[data-dd-applied-gift-card-remove]');
    if (giftRemoveBtn) {
      e.preventDefault();
      if (giftRemoveBtn.disabled) return;
      var giftBadge = giftRemoveBtn.closest('[data-dd-applied-gift-card]');
      if (!giftBadge) return;
      var rawAppId = (giftBadge.getAttribute('data-application-id') || '').trim();
      if (!rawAppId) return;
      /* Liquid emits the numeric Shopify ID; the Storefront API needs
         a GID. If a future Shopify version or another emitter
         pre-wraps with `gid://...` we leave it alone. */
      var applicationId = rawAppId.indexOf('gid://') === 0
        ? rawAppId
        : 'gid://shopify/AppliedGiftCard/' + rawAppId;
      if (_giftCardOpsInFlightHas(applicationId)) return;
      var giftContextRoot = getDiscountContextRoot(giftRemoveBtn);
      var giftFeedbackContainer = getScopedNode(giftContextRoot, CONTAINER_SEL);
      if (!(window.ddSFCart && typeof window.ddSFCart.remove === 'function')) {
        if (giftFeedbackContainer) {
          showFeedback(giftFeedbackContainer, "Couldn't remove gift card. Try again.", 'error');
        }
        return;
      }
      _giftCardOpsInFlightAdd(applicationId);
      giftRemoveBtn.disabled = true;
      giftBadge.classList.add('is-removing');
      window.ddSFCart.remove(applicationId)
        .then(function (res) {
          if (res && res.ok) {
            /* Same blindness story as apply: paint client-side from
               the SF response (now empty appliedGiftCards array, which
               clears chips and restores the subtotal to the cart total
               returned by SF — usually equal to /cart.js total once
               the gift card is gone). */
            if (window.ddSFCart && typeof window.ddSFCart.hydrate === 'function') {
              window.ddSFCart.hydrate({ state: res });
            }
            return refreshCartSectionsAfterGiftCard().then(function () {
              if (window.ddSFCart && typeof window.ddSFCart.hydrate === 'function') {
                window.ddSFCart.hydrate({ state: res });
              }
              var afterContainer = getScopedNode(giftContextRoot, CONTAINER_SEL) || giftFeedbackContainer;
              if (afterContainer) {
                showFeedback(afterContainer, 'Gift card removed.', 'success');
              }
            });
          }
          /* Failure: server still holds the badge, so re-enable the
             button rather than waiting for a section refresh that
             won't change anything. */
          giftRemoveBtn.disabled = false;
          giftBadge.classList.remove('is-removing');
          var msg = (res && res.message) || "Couldn't remove gift card. Try again.";
          if (giftFeedbackContainer) {
            showFeedback(giftFeedbackContainer, msg, 'error');
          }
        })
        .catch(function (err) {
          console.error('[DiscountCode] gift card remove error', err);
          giftRemoveBtn.disabled = false;
          giftBadge.classList.remove('is-removing');
          if (giftFeedbackContainer) {
            showFeedback(giftFeedbackContainer, "Couldn't remove gift card. Try again.", 'error');
          }
        })
        .then(function () {
          _giftCardOpsInFlightDelete(applicationId);
        });
      return;
    }

    /* Remove applied discount badge (strip) */
    var removeBtn = clickTarget.closest('.dd-cart-discount__remove');
    if (removeBtn) {
      e.preventDefault();
      var badge = removeBtn.closest('[data-dd-discount-badge]');
      var code = badge ? (badge.dataset.discountCode || '') : '';
      removeCode(code);
      return;
    }

    /* Apply discount code */
    var applyBtn = clickTarget.closest(APPLY_SEL);
    if (!applyBtn) return;
    var container = applyBtn.closest(CONTAINER_SEL);
    if (!container) return;
    var input = container.querySelector(INPUT_SEL);
    if (!input) return;
    applyCode(input.value, container);
  }, true);

  document.addEventListener('keydown', function (e) {
    var keyTarget = e && e.target;
    if (keyTarget && keyTarget.nodeType === 3) keyTarget = keyTarget.parentElement;
    if (!keyTarget || typeof keyTarget.closest !== 'function') return;
    if (!isEnterKey(e)) return;

    var input = keyTarget.closest(INPUT_SEL);
    if (input) {
      e.preventDefault();
      var inputContainer = input.closest(CONTAINER_SEL);
      if (!inputContainer) return;
      applyCode(input.value, inputContainer);
      return;
    }

    var applyBtn = keyTarget.closest(APPLY_SEL);
    if (!applyBtn) return;
    e.preventDefault();
    var btnContainer = applyBtn.closest(CONTAINER_SEL);
    if (!btnContainer) return;
    var btnInput = btnContainer.querySelector(INPUT_SEL);
    applyCode(btnInput ? btnInput.value : '', btnContainer);
  }, true);

  /* ── Restore saved discount code from localStorage ─
     The localStorage entry exists ONLY as a recovery breadcrumb for
     the narrow window between submit and resolution (see optimistic
     save at apply time + cleanup in success/rejected branches). At
     restore time we rule out three "stale entry" cases before
     prefilling, so the input never shows an unexplained orphaned
     code:
       1. The cart already has a `discount_code`-type badge applied —
          if any code is active, the optimistic save belongs to a
          completed apply that should have cleaned itself up; the
          chip is the source of truth, no prefill needed.
       2. The input already has a value (e.g. another script wrote to
          it first) — leave that alone.
       3. The saved value is empty/whitespace — delete and bail.
     If we DO prefill, we leave localStorage in place so a subsequent
     successful or rejected apply can clear it through the normal
     resolution path. */
  try {
    var savedCode = localStorage.getItem('CartDiscountcode');
    if (savedCode && savedCode.trim()) {
      var alreadyAppliedBadge = document.querySelector(
        '[data-dd-discount-badge][data-discount-type="discount_code"]'
      );
      if (alreadyAppliedBadge) {
        /* Stale: a code is already active on the cart. The chip
           shows it, so the input prefill would just confuse. Drop
           the saved entry now so it can't resurface on the next
           render either. */
        try { localStorage.removeItem('CartDiscountcode'); } catch (e2) {}
      } else {
        var initInput = document.querySelector(INPUT_SEL);
        if (initInput && !initInput.value) initInput.value = savedCode;
      }
    } else if (savedCode !== null) {
      /* Empty/whitespace-only saved value — purge it so it doesn't
         linger as dead state. */
      try { localStorage.removeItem('CartDiscountcode'); } catch (e3) {}
    }
  } catch (e) {}

  /* ── Pending-state reconciliation with server ──
     Fetches /cart.js once on init to surface pending codes (those
     stored on cart.discount_codes server-side with applicable:false)
     that the client has no other way to know about. Three reconcile
     cases:

       1. Server has a pending code we don't know about
          (cross-session: shopper applied yesterday, came back today)
          → add to _pendingCodes + sessionStorage. Renders as a
          pending chip on the next render, so the shopper sees it
          and can act on it (add a qualifying product, or remove).

       2. Our session has a pending code the server no longer holds
          (e.g., shopper checked out from another tab and the cart
          was reset) → drop from _pendingCodes + sessionStorage.

       3. Our session has a pending code that's now applicable:true
          on the server (a non-cart-aware activation we missed —
          rare, e.g., cart was modified by an app webhook) → leave
          it; renderChips() will detect the active badge and emit
          the activation message via the normal pending→activated
          transition.

     Cost: 1 GET to /cart.js per page load that hosts this script.
     Same-origin, no body, ~250-500 bytes typical. Within the per-
     action budget (rate-limit-awareness.mdc: <=3 requests; this is
     bookkeeping, not part of any user action). Skipped on cart-
     bearing pages where Liquid already exposes server state through
     badges, IF we're confident no pending codes exist. We can't be
     confident without checking, so we always run it. */
  function reconcilePendingFromCart() {
    if (typeof fetch !== 'function') return;
    fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      return r.ok ? r.json() : null;
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.discount_codes)) return;

      var serverPending = {};
      for (var i = 0; i < cart.discount_codes.length; i++) {
        var entry = cart.discount_codes[i];
        if (!entry || entry.applicable !== false) continue;
        var entryCode = (entry.code || '').toString();
        if (!entryCode) continue;
        var entryNorm = normalizeCode(entryCode);
        /* Skip if there's already an active badge for this code —
           defensive, since applicable:false shouldn't have a badge,
           but keeps us safe if Liquid + Storefront API ever
           disagree. */
        if (hasActiveDiscountCode(entryNorm)) continue;
        serverPending[entryNorm] = entryCode;
      }

      var changed = false;

      /* Drop session-pending entries the server no longer has. */
      var existingKeys = Object.keys(_pendingCodes);
      for (var k = 0; k < existingKeys.length; k++) {
        var ek = existingKeys[k];
        if (!serverPending[ek]) {
          delete _pendingCodes[ek];
          changed = true;
        }
      }

      /* Add server-pending entries not already in session. */
      var serverKeys = Object.keys(serverPending);
      for (var s = 0; s < serverKeys.length; s++) {
        var sk = serverKeys[s];
        if (!_pendingCodes[sk]) {
          _pendingCodes[sk] = serverPending[sk];
          changed = true;
        }
      }

      if (changed) {
        persistPendingCodes();
        renderChips();
      }
    }).catch(function (err) {
      /* Non-fatal — pending-state visibility degrades gracefully
         to in-session-only when /cart.js is unreachable. */
      try { console.warn('[dd-discount-code] reconcilePendingFromCart failed', err); } catch (_e) {}
    });
  }

  loadPendingCodesFromSession();

  window.ddDiscountChips = { render: renderChips };
  renderChips();

  /* Defer the reconcile call to the next animation frame — keeps the
     synchronous init path fast (renderChips() runs first off the
     in-memory pending state, then the network reconcile updates if
     needed). */
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(reconcilePendingFromCart);
  } else {
    setTimeout(reconcilePendingFromCart, 0);
  }
})();
